import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import CommentSection from '../CommentSection/CommentSection';
import PageMeta from '../SEO/PageMeta';
import './StudySyllabus.css';

/* ── Subjects, grouped + colored by their actual GS Paper ──────────────────
   Five meaningful colors instead of twelve arbitrary ones — the color
   encodes which General Studies paper the subject maps to.            ── */
const SUBJECT_META = {
  'History':                 { gs: 'GS-I',   color: '#f5c842' },
  'Geography':               { gs: 'GS-I',   color: '#f5c842' },
  'Art & Culture':           { gs: 'GS-I',   color: '#f5c842' },
  'Polity':                  { gs: 'GS-II',  color: '#4f8ef7' },
  'International Relations': { gs: 'GS-II',  color: '#4f8ef7' },
  'Social Issues':           { gs: 'GS-II',  color: '#4f8ef7' },
  'Governance':              { gs: 'GS-II',  color: '#4f8ef7' },
  'Economy':                 { gs: 'GS-III', color: '#1db954' },
  'Environment':             { gs: 'GS-III', color: '#1db954' },
  'Science & Technology':    { gs: 'GS-III', color: '#1db954' },
  'Ethics':                  { gs: 'GS-IV',  color: '#9b6dff' },
  'Current Affairs':         { gs: 'All GS', color: '#ff9933' },
};
const FALLBACK_META = { gs: '', color: '#ff9933' };

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
  return match ? match[1] : null;
}

/* ── Minimal inline icon set — one consistent shape per action, reused
   everywhere instead of a different emoji per spot. ────────────────────── */
function Icon({ d, size = 18, strokeWidth = 2, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={style}>
      <path d={d} />
    </svg>
  );
}
function IconPlayFilled({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
const PATH = {
  chevronLeft:  'M15 18l-6-6 6-6',
  chevronRight: 'M9 18l6-6-6-6',
  x:            'M18 6L6 18M6 6l12 12',
  menu:         'M3 6h18M3 12h18M3 18h18',
  check:        'M20 6L9 17l-5-5',
  folder:       'M3 7a2 2 0 0 1 2-2h4.5l2 2.5H19a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z',
};

function Skeletons({ count = 6, height = 130 }) {
  return (
    <>{[...Array(count)].map((_, i) => (
      <div key={i} className="skeleton" style={{ height, borderRadius: 14, marginBottom: 10 }} />
    ))}</>
  );
}

/* ── LEVEL 3: Video player ────────────────────────────────────────────────── */
function VideoPlayerPage({ subject, chapter }) {
  const navigate = useNavigate();
  const meta = SUBJECT_META[subject] || FALLBACK_META;

  const [videos,      setVideos]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeIdx,   setActiveIdx]   = useState(0);
  // Open by default on desktop, closed by default on mobile — it becomes
  // an overlay drawer there, so it shouldn't cover the video on load.
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth > 768
  );
  const [watched, setWatched] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`cf_watched_${subject}_${chapter}`) || '[]'); }
    catch { return []; }
  });
  const playerRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get('/syllabus/videos', { params: { subject, chapter } })
      .then(r => setVideos(r.data.videos || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [subject, chapter]);

  useEffect(() => {
    localStorage.setItem(`cf_watched_${subject}_${chapter}`, JSON.stringify(watched));
  }, [watched, subject, chapter]);

  // Tracks watched status by stable video _id, not array position — so
  // deleting/adding/reordering videos in the chapter can't desync progress.
  const markWatched = useCallback((id) => {
    if (!id) return;
    setWatched(prev => prev.includes(id) ? prev : [...prev, id]);
  }, []);

  const activeVideo = videos[activeIdx];
  const ytId = activeVideo ? getYouTubeId(activeVideo.youtubeUrl) : null;
  const nextVideo = videos[activeIdx + 1] || null;
  const videoTitle = `${chapter} ${subject} UPSC Video Lectures | CivilFox`;
  const videoDescription = activeVideo?.description || `Watch ${chapter} video lectures for ${subject} in the CivilFox UPSC syllabus.`;
  const videoPath = `/study-upsc-syllabus/${encodeURIComponent(subject)}/${encodeURIComponent(chapter)}`;

  // Only count watched IDs that belong to videos that still actually exist
  // in this chapter right now — stale IDs from deleted videos drop out on
  // their own instead of inflating or corrupting the count.
  const watchedCount = videos.filter(v => v._id && watched.includes(v._id)).length;

  const selectVideo = (idx) => {
    markWatched(activeVideo?._id);
    setActiveIdx(idx);
    if (window.innerWidth <= 768) setSidebarOpen(false);
  };
  const goNext = () => {
    if (activeIdx < videos.length - 1) {
      markWatched(activeVideo?._id);
      setActiveIdx(i => i + 1);
      if (window.innerWidth <= 768) playerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) return (
    <div className="ss-page fade-in">
      <button className="ss-back" style={{ marginBottom: 16 }} onClick={() => navigate(`/study-upsc-syllabus/${encodeURIComponent(subject)}`)}>
        <Icon d={PATH.chevronLeft} size={16} /><span>{subject}</span>
      </button>
      <Skeletons count={3} height={200} />
    </div>
  );

  if (videos.length === 0) return (
    <div className="ss-page fade-in">
      <button className="ss-back" style={{ marginBottom: 16 }} onClick={() => navigate(`/study-upsc-syllabus/${encodeURIComponent(subject)}`)}>
        <Icon d={PATH.chevronLeft} size={16} /><span>{subject}</span>
      </button>
      <div className="ss-empty">
        <div className="ss-empty-icon"><Icon d={PATH.folder} size={44} strokeWidth={1.5} /></div>
        <h3>Videos coming soon</h3>
        <p>We're uploading lectures for this chapter. Check back shortly.</p>
      </div>
    </div>
  );

  return (
    <div className="ss-player-shell">
      <PageMeta
        title={videoTitle}
        description={videoDescription}
        path={videoPath}
        robots={videos.length ? 'index, follow' : 'noindex, follow'}
      />

      {/* Top bar */}
      <div className="ss-player-topbar" style={{ '--ss-color': meta.color }}>
        <button className="ss-back-btn" onClick={() => navigate(`/study-upsc-syllabus/${encodeURIComponent(subject)}`)}>
          <Icon d={PATH.chevronLeft} size={16} /><span>{subject}</span>
        </button>
        <div className="ss-player-topbar-info">
          <div className="ss-topbar-chapter">{chapter}</div>
          <div className="ss-topbar-subject">{subject} · {meta.gs}</div>
        </div>
        <div className="ss-topbar-progress">
          <span>{watchedCount}/{videos.length} watched</span>
          <div className="ss-progress-bar">
            <div className="ss-progress-fill" style={{ width: `${(watchedCount / videos.length) * 100}%` }} />
          </div>
        </div>
        <button className="ss-icon-btn" onClick={() => setSidebarOpen(o => !o)}>
          {sidebarOpen ? <Icon d={PATH.x} size={15} /> : <Icon d={PATH.menu} size={15} />}
          <span>{sidebarOpen ? 'Hide list' : 'Show list'}</span>
        </button>
      </div>

      {/* Body */}
      <div className={`ss-player-body ${sidebarOpen ? '' : 'sidebar-hidden'}`}>

        {/* Backdrop — only visible/interactive on mobile, where the
            sidebar becomes an overlay drawer rather than a column. */}
        {sidebarOpen && <div className="ss-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

        {/* LEFT SIDEBAR / MOBILE DRAWER */}
        {sidebarOpen && (
          <aside className="ss-video-sidebar" style={{ '--ss-color': meta.color }}>
            <div className="ss-sidebar-header">
              <div>
                <div className="ss-sidebar-title">Chapter videos</div>
                <div className="ss-sidebar-count">{videos.length} lectures</div>
              </div>
              <button className="ss-sidebar-close" aria-label="Close video list" onClick={() => setSidebarOpen(false)}>
                <Icon d={PATH.x} size={18} />
              </button>
            </div>
            <div className="ss-sidebar-list">
              {videos.map((v, i) => {
                const vid = getYouTubeId(v.youtubeUrl);
                const isActive  = i === activeIdx;
                const isWatched = v._id ? watched.includes(v._id) : false;
                return (
                  <button key={v._id || i}
                    className={`ss-sidebar-item ${isActive ? 'active' : ''} ${isWatched && !isActive ? 'watched' : ''}`}
                    onClick={() => selectVideo(i)}>
                    <div className="ss-sidebar-thumb">
                      {vid
                        ? <img src={`https://img.youtube.com/vi/${vid}/default.jpg`} alt="" />
                        : <div className="ss-sidebar-thumb-ph"><Icon d={PATH.folder} size={18} strokeWidth={1.5} /></div>}
                      {isActive && <div className="ss-thumb-playing"><IconPlayFilled size={16} /></div>}
                    </div>
                    <div className="ss-sidebar-item-body">
                      <div className="ss-sidebar-marker">
                        {isWatched
                          ? <span className="ss-check"><Icon d={PATH.check} size={13} strokeWidth={2.5} /></span>
                          : <span className="ss-num">{String(i + 1).padStart(2, '0')}</span>}
                      </div>
                      <div className="ss-sidebar-text">
                        <div className="ss-sidebar-item-title">{v.title}</div>
                        {v.duration && <div className="ss-sidebar-item-dur">{v.duration}</div>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>
        )}

        {/* RIGHT: PLAYER */}
        <main className="ss-player-main" ref={playerRef}>
          <div className="ss-embed-wrap">
            {ytId ? (
              <iframe key={ytId} className="ss-embed-iframe"
                src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1`}
                title={activeVideo?.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen onLoad={() => markWatched(activeVideo?._id)} />
            ) : (
              <div className="ss-no-video-box">
                <Icon d={PATH.folder} size={40} strokeWidth={1.5} />
                <p>Video not available yet</p>
              </div>
            )}
          </div>

          <div className="ss-video-info-row">
            <div>
              <h1 className="ss-video-title-main">{activeVideo?.title || chapter}</h1>
              {activeVideo?.description && <p className="ss-video-desc-main">{activeVideo.description}</p>}
              <div className="ss-video-tags">
                {activeVideo?.duration && <span className="ss-tag">{activeVideo.duration}</span>}
                <span className="ss-tag">Lecture {activeIdx + 1} of {videos.length}</span>
                <span className="ss-tag" style={{ color: meta.color, borderColor: meta.color }}>{subject}</span>
              </div>
            </div>
            {ytId && (
              <a className="ss-yt-pill" href={`https://www.youtube.com/watch?v=${ytId}`}
                target="_blank" rel="noopener noreferrer">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8z"/>
                  <path d="M9.8 15.5V8.5l6.2 3.5-6.2 3.5z" fill="white"/>
                </svg>
                Watch on YouTube
              </a>
            )}
          </div>

          {nextVideo && (
            <div className="ss-up-next" onClick={goNext}>
              <div className="ss-up-next-label">Up next</div>
              <div className="ss-up-next-content">
                {getYouTubeId(nextVideo.youtubeUrl) && (
                  <img src={`https://img.youtube.com/vi/${getYouTubeId(nextVideo.youtubeUrl)}/default.jpg`}
                    alt="" className="ss-up-next-thumb" />
                )}
                <div className="ss-up-next-info">
                  <div className="ss-up-next-num">Lecture {activeIdx + 2}</div>
                  <div className="ss-up-next-title">{nextVideo.title}</div>
                  {nextVideo.duration && <div className="ss-up-next-dur">{nextVideo.duration}</div>}
                </div>
                <div className="ss-up-next-arrow"><Icon d={PATH.chevronRight} size={20} /></div>
              </div>
            </div>
          )}

          {/* Comments — one thread per video, keyed to activeVideo._id */}
          {activeVideo?._id && (
            <div className="ss-comments-wrap">
              <CommentSection
                cardId={activeVideo._id}
                isLoggedIn={true}
              />
            </div>
          )}

          {!nextVideo && watchedCount === videos.length && (
            <div className="ss-chapter-done">
              <div className="ss-done-icon"><Icon d={PATH.check} size={36} strokeWidth={2.5} /></div>
              <h3>Chapter complete</h3>
              <p>You've watched all {videos.length} lectures in <strong>{chapter}</strong></p>
              <button className="btn btn-primary" onClick={() => navigate(`/study-upsc-syllabus/${encodeURIComponent(subject)}`)}>
                Back to {subject}
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

/* ── LEVEL 2: Chapter list ────────────────────────────────────────────────── */
function ChapterListPage({ subject }) {
  const navigate = useNavigate();
  const meta = SUBJECT_META[subject] || FALLBACK_META;
  const [chapters, setChapters] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const title = `${subject} UPSC Syllabus: Chapters & Video Lectures | CivilFox`;
  const description = `Explore ${subject} in the UPSC syllabus with chapter-wise video lectures and study resources on CivilFox.`;

  useEffect(() => {
    setLoading(true);
    api.get('/syllabus/chapters', { params: { subject } })
      .then(r => setChapters(r.data.chapters || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [subject]);

  return (
    <div className="ss-page fade-in">
      <PageMeta
        title={title}
        description={description}
        path={`/study-upsc-syllabus/${encodeURIComponent(subject)}`}
        robots={!loading && chapters.length ? 'index, follow' : 'noindex, follow'}
      />
      <div className="ss-breadcrumb">
        <button className="ss-back" onClick={() => navigate('/study-upsc-syllabus')}>
          <Icon d={PATH.chevronLeft} size={16} /><span>All subjects</span>
        </button>
      </div>
      <div className="ss-section-header" style={{ '--ss-color': meta.color }}>
        <div>
          <h1 className="ss-title">{subject}</h1>
          <p className="ss-sub">
            <span className="ss-gs-tag">{meta.gs}</span>
            <span> · Select a chapter to watch lectures</span>
          </p>
        </div>
      </div>
      {loading ? <Skeletons count={8} height={72} /> : chapters.length === 0 ? (
        <div className="ss-empty">
          <div className="ss-empty-icon"><Icon d={PATH.folder} size={44} strokeWidth={1.5} /></div>
          <h3>Chapters coming soon</h3>
          <p>We're building the syllabus for this subject. Stay tuned.</p>
        </div>
      ) : (
        <div className="ss-chapter-list">
          {chapters.map((ch, i) => (
            <Link key={ch.name} className="ss-chapter-item" style={{ '--ss-color': meta.color }}
              to={`/study-upsc-syllabus/${encodeURIComponent(subject)}/${encodeURIComponent(ch.name)}`}>
              <span className="ss-chapter-num">{String(i + 1).padStart(2, '0')}</span>
              <span className="ss-chapter-name">{ch.name}</span>
              <span className="ss-chapter-count">{ch.videoCount || 0} videos</span>
              <span className="ss-chapter-arrow"><Icon d={PATH.chevronRight} size={18} /></span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── LEVEL 1: Subject grid ────────────────────────────────────────────────── */
function SubjectGridPage() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const isLoggedIn = !!localStorage.getItem('vidyacards_token');
  const title = 'UPSC Video Syllabus: Free Subject-wise Lectures | CivilFox';
  const description = 'Browse a structured UPSC Civil Services video syllabus with subject-wise and chapter-wise lectures for General Studies preparation.';

  useEffect(() => {
    api.get('/syllabus/counts')
      .then(r => setCounts(r.data.counts || {}))
      .catch(console.error);
  }, []);

  return (
    <div className="ss-page fade-in">
      <PageMeta
        title={title}
        description={description}
        path="/study-upsc-syllabus"
      />
      <div className="ss-breadcrumb">
        <button className="ss-back" onClick={() => navigate(isLoggedIn ? '/' : '/login')}>
          <Icon d={PATH.chevronLeft} size={16} /><span>{isLoggedIn ? 'Dashboard' : 'Log in'}</span>
        </button>
      </div>
      <div className="ss-hero">
        <h1 className="ss-hero-title">UPSC Video Syllabus</h1>
        <p className="ss-hero-sub">Structured video lectures · Hosted on YouTube · Watch anywhere</p>
      </div>
      <div className="ss-subject-grid">
        {Object.entries(SUBJECT_META).map(([subject, meta]) => (
          <Link key={subject} className="ss-subject-card" style={{ '--ss-color': meta.color }}
            to={`/study-upsc-syllabus/${encodeURIComponent(subject)}`}>
            <span className="ss-gs-tag">{meta.gs}</span>
            <span className="ss-subject-name">{subject}</span>
            {counts[subject] > 0 && <span className="ss-subject-count">{counts[subject]} videos</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ── Root switch ──────────────────────────────────────────────────────────── */
export default function StudySyllabus() {
  const { subject: sp, chapter: cp } = useParams();
  const subject = sp ? decodeURIComponent(sp) : null;
  const chapter = cp ? decodeURIComponent(cp) : null;
  if (subject && chapter) return <VideoPlayerPage subject={subject} chapter={chapter} />;
  if (subject)            return <ChapterListPage subject={subject} />;
  return                         <SubjectGridPage />;
}